import {
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogBackdrop,
  DialogCloseTrigger,
  DialogPositioner,
  Button,
  VStack,
  Text,
  HStack,
  Badge,
  Box,
} from '@chakra-ui/react'
import { Zap, ExternalLink } from 'lucide-react'
import { usePricingTiers } from '@/shared/hooks/usePricingTiers'
import { useSubscription } from '@/shared/hooks/useSubscription'

interface UpgradeModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  reason: string
  resource?: string
}

export function UpgradeModal({ open, onOpenChange, reason, resource: _resource }: UpgradeModalProps) {
  const { data: tiers } = usePricingTiers()
  const { tier: currentTier } = useSubscription()

  // Suggérer le tier suivant
  const suggestedTier = tiers?.find((t) => {
    if (currentTier === 'essential') return t.tier_key === 'professional'
    if (currentTier === 'professional') return t.tier_key === 'integral'
    return false
  })

  const handleUpgrade = () => {
    window.location.href = '/#pricing'
  }

  const handleManageSubscription = () => {
    window.open('https://billing.stripe.com/p/login/test_5kQ14pb8Bh1Ob5X7N55os00', '_blank')
  }

  return (
    <DialogRoot open={open} onOpenChange={({ open }) => onOpenChange(open)} size="lg">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="lg">
        <DialogHeader>
          <DialogTitle>
            <HStack gap={2}>
              <Zap size={24} color="#f59e0b" />
              <Text>Limite atteinte</Text>
            </HStack>
          </DialogTitle>
          <DialogCloseTrigger />
        </DialogHeader>

        <DialogBody>
          <VStack align="stretch" gap={4}>
            <Text>{reason}</Text>

            {suggestedTier && (
              <Box p={4} borderWidth="1px" borderRadius="lg" bg="brand.50">
                <VStack align="stretch" gap={2}>
                  <HStack justify="space-between">
                    <Text fontWeight="bold" fontSize="lg">
                      {suggestedTier.tier_name}
                    </Text>
                    <Badge colorPalette="brand">Recommandé</Badge>
                  </HStack>
                  <Text fontSize="2xl" fontWeight="bold" color="brand.600">
                    {suggestedTier.price_monthly_ht.toLocaleString('fr-FR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}€ HT/mois
                  </Text>
                  <VStack align="stretch" gap={1} mt={2}>
                    <HStack>
                      <Text fontSize="sm" fontWeight="medium">
                        Résidences:
                      </Text>
                      <Text fontSize="sm">
                        {suggestedTier.max_residences === -1
                          ? 'Illimité'
                          : suggestedTier.max_residences}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" fontWeight="medium">
                        Utilisateurs:
                      </Text>
                      <Text fontSize="sm">
                        {suggestedTier.max_users === -1 ? 'Illimité' : suggestedTier.max_users}
                      </Text>
                    </HStack>
                    <HStack>
                      <Text fontSize="sm" fontWeight="medium">
                        Lots:
                      </Text>
                      <Text fontSize="sm">
                        {suggestedTier.max_lots === -1 ? 'Illimité' : suggestedTier.max_lots}
                      </Text>
                    </HStack>
                  </VStack>
                </VStack>
              </Box>
            )}
          </VStack>
        </DialogBody>

        <DialogFooter>
          <HStack gap={3}>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button variant="outline" onClick={handleManageSubscription}>
              Gérer mon abonnement
              <ExternalLink size={16} />
            </Button>
            <Button colorPalette="brand" onClick={handleUpgrade}>
              <Zap size={16} />
              Voir les formules
            </Button>
          </HStack>
        </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}
